/* ─────────────────────────────────────────────
   PACEL.LV Landing · Interactions
   Scoped to elements inside .pacel-lv-landing.
   Idempotent: safe to load multiple times in the
   same page (e.g. via several custom-liquid sections).
   ───────────────────────────────────────────── */

(() => {
  'use strict';
  if (window.__pacelLvLandingInit) return;
  window.__pacelLvLandingInit = true;

  const ROOT_SELECTOR = '.pacel-lv-landing';

  const init = () => {
    const roots = document.querySelectorAll(ROOT_SELECTOR);
    if (!roots.length) return;

    /* ─── Reveal-on-scroll ─── */
    const revealSelector = [
      '.el-review',
      '.el-case',
      '.el-bigstat',
      '.el-measure__visual',
      '.el-booking__form',
      '.el-video-embed',
      '.eks-feature',
    ].join(',');

    const reveals = [];
    roots.forEach((root) => {
      root.querySelectorAll(revealSelector).forEach((el) => {
        if (el.classList.contains('el-reveal')) return;
        el.classList.add('el-reveal');
        reveals.push(el);
      });
    });

    if ('IntersectionObserver' in window && reveals.length) {
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-visible');
              io.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
      reveals.forEach((el) => io.observe(el));
    } else {
      reveals.forEach((el) => el.classList.add('is-visible'));
    }

    /* ─── Big-stat count-up ─── */
    const bigNums = [];
    roots.forEach((root) => {
      root
        .querySelectorAll('.el-bigstat__num[data-count]')
        .forEach((el) => bigNums.push(el));
    });
    if ('IntersectionObserver' in window && bigNums.length) {
      const numObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const el = entry.target;
            const target = parseInt(el.dataset.count, 10);
            if (!Number.isFinite(target)) {
              numObserver.unobserve(el);
              return;
            }
            const duration = 1400;
            const start = performance.now();
            const tick = (now) => {
              const t = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - t, 3);
              const value = Math.floor(target * eased);
              if (el.firstChild && el.firstChild.nodeType === Node.TEXT_NODE) {
                el.firstChild.nodeValue = String(value);
              } else {
                el.insertBefore(document.createTextNode(String(value)), el.firstChild);
              }
              if (t < 1) requestAnimationFrame(tick);
              else {
                if (el.firstChild && el.firstChild.nodeType === Node.TEXT_NODE) {
                  el.firstChild.nodeValue = String(target);
                }
              }
            };
            requestAnimationFrame(tick);
            numObserver.unobserve(el);
          });
        },
        { threshold: 0.5 }
      );
      bigNums.forEach((el) => numObserver.observe(el));
    }

    /* ─── Smooth-scroll for in-page links ─── */
    roots.forEach((root) => {
      root.querySelectorAll('a[href^="#"]').forEach((a) => {
        a.addEventListener('click', (e) => {
          const id = a.getAttribute('href');
          if (!id || id === '#') return;
          const target = document.querySelector(id);
          if (!target) return;
          e.preventDefault();
          const offset = parseInt(
            getComputedStyle(document.body)
              .getPropertyValue('--header-group-height') || '0',
            10
          ) || 72;
          const top = target.getBoundingClientRect().top + window.scrollY - offset - 16;
          window.scrollTo({ top, behavior: 'smooth' });
        });
      });
    });

    /* ─── Booking form: progressive enhancement only ─── */
    /* The form is a real Shopify {% form 'contact' %} which does a full POST.
       We do NOT preventDefault — Shopify handles success/error redirects.
       We just disable the button + swap label so the user gets feedback. */
    roots.forEach((root) => {
      root.querySelectorAll('.el-booking__form').forEach((form) => {
        form.addEventListener('submit', () => {
          const btn = form.querySelector('button[type="submit"]');
          if (!btn) return;
          btn.disabled = true;
          const original = btn.innerHTML;
          btn.dataset.originalLabel = original;
          btn.innerHTML = 'Sūta...';
          // If the form errors out and the page reloads with .has-error, the
          // button is fresh again on next render, so no further restore needed.
        });
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
